import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  JoinOrganizationRequestDto,
  JoinOrganizationResponseDto,
  OrganizationRequestDto,
  OrganizationResponseDto,
  OrgTypesListResponseDto,
  CollaborativeOnboardingRequestDto,
  CollaborativeOnboardingResponseDto,
  DiscoveryResponseDto,
  JoinWorkspaceResponseDto,
  AcceptInvitationResponseDto,
  SendOrgInvitationsResponseDto,
  OrgInviteRowDto,
  PresignedUploadRequestDto,
  PresignedUploadResponseDto,
  UpdateUserProfileDto,
  UpdateUserProfileResponseDto
} from '../models/organization.dto';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  private readonly apiUrl = `${environment.apiHost}/api`;

  constructor(private readonly http: HttpClient) { }

  // The HTTP interceptor will automatically add the authorization token
  createOrganization(organization: OrganizationRequestDto): Observable<OrganizationResponseDto> {
    return this.http.post<OrganizationResponseDto>(
      `${this.apiUrl}/organizations/`,
      organization
    );
  }

  joinOrganization(payload: JoinOrganizationRequestDto): Observable<JoinOrganizationResponseDto> {
    return this.http.post<JoinOrganizationResponseDto>(`${this.apiUrl}/organizations/join/`, payload);
  }

  /** Fetch available organization types. GET /api/organization-types/ */
  getOrganizationTypes(): Observable<OrgTypesListResponseDto> {
    return this.http.get<OrgTypesListResponseDto>(`${this.apiUrl}/organization-types/`);
  }

  /**
   * Complete the collaborative client onboarding in a single atomic call.
   * The backend validates the JWT, creates the org, links it to the agency, and
   * activates the feature flags encoded in the invitation token.
   * POST /api/organizations/collaborative-onboarding/
   */
  collaborativeOnboarding(body: CollaborativeOnboardingRequestDto): Observable<CollaborativeOnboardingResponseDto> {
    return this.http.post<CollaborativeOnboardingResponseDto>(
      `${this.apiUrl}/organizations/collaborative-onboarding/`,
      body
    );
  }

  // ─── Company-discovery onboarding ────────────────────────────────────────────

  /** Workspaces the signed-in user can join (domain matches + pending invites). */
  getDiscovery(): Observable<DiscoveryResponseDto> {
    return this.http.get<DiscoveryResponseDto>(`${this.apiUrl}/organizations/discovery/`);
  }

  /** Join a domain-matched workspace; may return an immediate join or a pending approval. */
  joinDomainWorkspace(organizationId: string): Observable<JoinWorkspaceResponseDto> {
    return this.http.post<JoinWorkspaceResponseDto>(
      `${this.apiUrl}/organizations/${organizationId}/join-domain/`,
      {}
    );
  }

  /** Accept an invitation addressed to the signed-in user. */
  acceptInvitation(invitationId: string): Observable<AcceptInvitationResponseDto> {
    return this.http.post<AcceptInvitationResponseDto>(
      `${this.apiUrl}/organizations/invitations/${invitationId}/accept/`,
      {}
    );
  }

  /** Decline an invitation addressed to the signed-in user. */
  declineInvitation(invitationId: string): Observable<AcceptInvitationResponseDto> {
    return this.http.post<AcceptInvitationResponseDto>(
      `${this.apiUrl}/organizations/invitations/${invitationId}/decline/`,
      {}
    );
  }

  /** Create & send organization invitations. POST /api/organizations/{id}/invitations/ */
  sendOrgInvitations(organizationId: string, invites: OrgInviteRowDto[]): Observable<SendOrgInvitationsResponseDto> {
    return this.http.post<SendOrgInvitationsResponseDto>(
      `${this.apiUrl}/organizations/${organizationId}/invitations/`,
      { invites }
    );
  }

  // ─── First-time setup wizard ─────────────────────────────────────────────────

  /** Get a presigned R2 upload URL (reuses the attachments endpoint; no ticket needed). */
  getPresignedUploadUrl(body: PresignedUploadRequestDto): Observable<PresignedUploadResponseDto> {
    return this.http.post<PresignedUploadResponseDto>(
      `${this.apiUrl}/attachments/presigned-url/`,
      body
    );
  }

  /** Upload a file directly to R2 via the presigned URL (no auth header — URL is signed). */
  uploadFileToPresignedUrl(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: { 'Content-Type': file.type }
    });
  }

  /** Update the signed-in user's profile (name + avatar). PATCH /api/users/{id}/ */
  updateUserProfile(userId: string, body: UpdateUserProfileDto): Observable<UpdateUserProfileResponseDto> {
    return this.http.patch<UpdateUserProfileResponseDto>(
      `${this.apiUrl}/users/${userId}/`,
      body
    );
  }

  /** Fetch the signed-in user's profile (email + name for prefill). GET /api/users/{id}/ */
  getUser(userId: string): Observable<UpdateUserProfileResponseDto> {
    return this.http.get<UpdateUserProfileResponseDto>(`${this.apiUrl}/users/${userId}/`);
  }

}
